/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect, useState } from 'react';
import { AppLayout } from './components/layout/AppLayout';
import { ChatInterface } from './components/chat/ChatInterface';
import { AdminPanel } from './components/admin/AdminPanel';
import { UsersAdminPanel } from './components/admin/UsersAdminPanel';
import { PreviewPanel } from './components/preview/PreviewPanel';
import { Landing } from './components/landing/Landing';
import { AuthModal, type AuthMode } from './components/auth/AuthModal';
import { ContractsPage } from './components/contracts/ContractsPage';
import { useAuthStore } from './store/auth';
import { setOn401 } from './services/api';
import { Loader2 } from 'lucide-react';

export type AppView = 'chat' | 'contracts';

export default function App() {
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const init = useAuthStore((s) => s.init);
  const setUser = useAuthStore((s) => s.setUser);

  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [view, setView] = useState<AppView>('chat');

  useEffect(() => {
    init();
    setOn401(() => {
      setUser(null);
      setAuthOpen(true);
      setAuthMode('login');
    });
  }, [init, setUser]);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c] text-white/40">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Landing
          onSignIn={() => { setAuthMode('login'); setAuthOpen(true); }}
          onSignUp={() => { setAuthMode('register'); setAuthOpen(true); }}
        />
        <AuthModal
          open={authOpen}
          mode={authMode}
          onClose={() => setAuthOpen(false)}
          onModeChange={setAuthMode}
        />
      </>
    );
  }

  return (
    <>
      <AppLayout view={view} onViewChange={setView}>
        {view === 'chat' ? <ChatInterface /> : <ContractsPage />}
      </AppLayout>
      <AdminPanel />
      <UsersAdminPanel />
      <PreviewPanel />
    </>
  );
}
